import { DeleteOutlined } from "@ant-design/icons";
import cuid from "cuid";
import { FieldArray } from "formik";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Radio from "shared/components/Form/Radio";
import Input from "shared/components/Input";
import Switch from "shared/components/Input/Switch";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { STORES } from "utils/api/graphql/queries/stores";
import { USERS } from "utils/api/graphql/queries/users";
import { GENDER, SKILL_TYPES, USER_TYPES } from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import SkillDepartmentField from "../../shared/SkillDepartmentField";
import { handleSkillTypeChange } from "../../shared/skillTypeOnChange";

const Advisor = ({ formProps }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const FORM = t("FORM", { returnObjects: true });
  const InputSwitchSkill = (index) => ({
    [SKILL_TYPES.AGENCY]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindArrayInputProps({
          parent: "skills",
          index,
          name: "value",
          ...formProps,
        })}
      />
    ),
    [SKILL_TYPES.ESTABLISHMENT]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindArrayInputProps({
          parent: "skills",
          index,
          name: "value",
          ...formProps,
        })}
      />
    ),
    [SKILL_TYPES.ZIPCODE]: (
      <Input
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindArrayInputProps({
          parent: "skills",
          index,
          name: "value",
          ...formProps,
        })}
      />
    ),
    [SKILL_TYPES.STORE]: (
      <Select
        type="async"
        node={{
          query: STORES,
          variables: {},
          accessors: {
            root: "stores.data",
          },
        }}
        placeholder={FORM.SKILLS.VALUE}
        label={FORM.SKILLS.VALUE}
        {...bindArrayInputProps({
          parent: "skills",
          index,
          name: "value",
          ...formProps,
        })}
      />
    ),
    [SKILL_TYPES.DEPARTMENT]: (
      <SkillDepartmentField
        formProps={formProps}
        index={index}
        excludeLabel={get(FORM, "SKILLS.EXCLUDE", "Exclure")}
        valueLabel={FORM.SKILLS.VALUE}
      />
    ),
  });
  return [
    <div key="advisor-user-form" className="grid--1">
      <Card key="user" type={"simple"} head title={FORM.GENERAL_INFO}>
        <div className="grid--2">
          <Select
            mode="multiple"
            type="async"
            node={{
              query: USERS,
              variables: {
                where: {
                  isActive: true,
                  meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type },
                },
              },
              accessors: {
                root: "users.data",
                label: {
                  separator: " ",
                  keys: ["firstname", "lastname"],
                },
                value: "id",
              },
            }}
            placeholder={FORM.SUPERVISOR}
            label={FORM.SUPERVISOR}
            {...bindInputProps({
              name: "supervisors",
              ...formProps,
            })}
            onChange={(value) => formProps.setFieldValue("supervisors", value)}
          />
          <div></div>
          <Input
            placeholder={FORM.NETWORK}
            label={FORM.NETWORK}
            {...bindInputProps({ name: "meta.network", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.AGENCY}
            label={FORM.AGENCY}
            {...bindInputProps({ name: "meta.agency", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.AGENCY_EMAIL}
            label={FORM.AGENCY_EMAIL}
            {...bindInputProps({ name: "meta.emailAgency", ...formProps })}
          />
          <Radio
            isHorizontal={false}
            label={FORM.CIVILITY.LABEL}
            options={[
              {
                label: FORM.CIVILITY.MALE,
                value: GENDER.MALE,
              },
              {
                label: FORM.CIVILITY.FEMALE,
                value: GENDER.FEMALE,
              },
            ]}
            {...bindInputProps({ name: "gender", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.FIRST_NAME}
            label={FORM.FIRST_NAME}
            {...bindInputProps({ name: "firstname", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.LAST_NAME}
            label={FORM.LAST_NAME}
            {...bindInputProps({ name: "lastname", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.PHONE_AGENCY}
            label={FORM.PHONE_AGENCY}
            {...bindInputProps({ name: "phone", ...formProps })}
          />
          <Input
            placeholder={FORM.EMAIL}
            label={FORM.EMAIL}
            {...bindInputProps({ name: "email", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.ADDRESS}
            label={FORM.ADDRESS}
            {...bindInputProps({ name: "address", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.ZIP_CODE}
            label={FORM.ZIP_CODE}
            {...bindInputProps({ name: "zipCode", ...formProps })}
            required
          />
          <Input
            placeholder={FORM.CITY}
            label={FORM.CITY}
            {...bindInputProps({ name: "city", ...formProps })}
            required
          />
          <Switch
            label="External Distribution Partnership"
            checkedChildren={FORM.OPTIONS.YES}
            unCheckedChildren={FORM.OPTIONS.NO}
            defaultChecked={get(
              formProps.values,
              "params.extDistPartner",
              false,
            )}
            onChange={(e) =>
              formProps.setFieldValue("params.extDistPartner", e)
            }
          />
        </div>
      </Card>
      <Card title={FORM.SKILLS.TITLE} divider={false} key="skills">
        <div className="grid--1">
          <FieldArray
            name={"skills"}
            render={(arrayHelpers) => [
              get(formProps, "values.skills", []).map((skill, i) => {
                const skillType = get(skill, "type");
                const isDepartment = skillType === SKILL_TYPES.DEPARTMENT;

                return [
                  <div
                    className={isDepartment ? "grid--1 gap-16" : "grid--2"}
                    key={`row-${i}`}
                  >
                    <Select
                      options={Object.entries(SKILL_TYPES).map(
                        ([key, value]) => ({
                          label: get(FORM, `SKILLS.TYPES.${key}`, key),
                          value,
                        }),
                      )}
                      placeholder={FORM.SKILLS.TYPE}
                      label={FORM.SKILLS.TYPE}
                      value={skillType}
                      onChange={(value) =>
                        handleSkillTypeChange(formProps, i, value)
                      }
                    />
                    {InputSwitchSkill(i)[skillType]}
                    <div className="d-flex align--end">
                      <Button
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                        type="danger--outlined"
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  </div>,
                ];
              }),
              <Button
                key="add-row"
                onClick={() => {
                  arrayHelpers.push({ id: cuid(), type: null, value: null });
                }}
                type="primary"
              >
                <BiPlus size={24} />
              </Button>,
            ]}
          />
        </div>
      </Card>
    </div>,
  ];
};

export default Advisor;
